import {ConverterError, ValidationErrors} from "./converter-errors"
import {useIntl} from "react-intl"
import * as Sentry from "@sentry/react"
import "../styles/sentry.css"


const MAX_ERRORS = 10

export function useWithSentryReport() {
    const intl = useIntl()
    const nbsp = "\u00A0"

    return (fn: (...args: any[]) => void | Promise<void>, errorMessage: { id: string }, onException?: () => void) => {
        return async (...args: any[]) => {
            try {
                await fn(...args)
            } catch (e: any) {
                console.error(e.message)
                onException?.()
                if (e instanceof ConverterError)
                    return  // Converter semantics errors go to the Creator specific UX rather than raising crash reports
                showSentryDialog(e, errorMessage)
            }
        }
    }

    /**
     * Documentation at https://docs.sentry.io/platforms/javascript/user-feedback/configuration/#crash-report-modal
     */
    function showSentryDialog(e: Error, errorMessage: { id: string }) {
        const eventId = Sentry.captureException(e)
        Sentry.showReportDialog({
            eventId,
            user: { name: "feedback", email: "feedback@gedcomcreator.com" },
            lang: intl.locale,
            title: intl.formatMessage({ id: errorMessage.id }) || intl.formatMessage({ id: "error.something.wrong" }),
            subtitle: e instanceof ValidationErrors ? intl.formatMessage({ id: "error.bundle" }) : "",
            // Form
            subtitle1: e instanceof ValidationErrors ? "" : (nbsp + intl.formatMessage({ id: "error.alert" }, { alert: "🔔" })),
            subtitle2: e instanceof ValidationErrors ? ""
                : intl.formatMessage({ id: "error.feedback.help" }) + " — " + intl.formatMessage({ id: "error.feedback.email" }),
            onLoad: () => styleDialog(e),
            labelSubmit: intl.formatMessage({ id: "contact.submit" }),
            successMessage: intl.formatMessage({ id: "contact.title" }),
            labelClose: intl.formatMessage({ id: "contact.close" }),
        })
    }

    function styleDialog(e: unknown) {
        document.addEventListener("keydown", escapeHandler)
        const formElement = document.querySelector<HTMLFormElement>(".sentry-error-embed > form")!
        if (e instanceof ValidationErrors) {
            // If errors is a bundle, hide the form and make list...
            const formContent = document.querySelector<HTMLDivElement>(".form-content")
            const formSubmit = document.querySelector<HTMLDivElement>(".form-submit")
            formElement?.removeChild(formContent!)
            formElement?.removeChild(formSubmit!)
            for (const error of e.errors.slice(0, MAX_ERRORS)) formElement!.appendChild(Object.assign(document.createElement("p"), {
                innerHTML: error.message,
                className: "message-error"
            }))
            if (e.errors.length > MAX_ERRORS) formElement!.appendChild(Object.assign(document.createElement("p"), {
                textContent: intl.formatMessage({id: "converter.bundle.more"}, {nErrors: Math.abs(MAX_ERRORS - e.errors.length)}),
                className: "message-error",
                style: "font-weight: bold; text-align: right !important;",
            }))
        } else {
            // Placeholder hints
            const email = document.querySelector<HTMLTextAreaElement>("#id_email")
            const textarea = document.querySelector<HTMLTextAreaElement>("#id_comments")
            textarea!.placeholder = nbsp + intl.formatMessage({id: "error.feedback.placeholder"})
            email!.placeholder = "You can leave your email if you’d like us to get back to you about this."
            // Disable submit until comments
            const submitButton = formElement.querySelector<HTMLButtonElement>('button[type="submit"]')!
            // submitButton.classList.add("ui", "button")
            const submitEnabled = () => {
                const isDisabled = textarea!.value.trim() === ""
                submitButton.classList.toggle("disabled", isDisabled)
            }
            submitEnabled()
            textarea!.addEventListener("input", submitEnabled)
        }
    }

    function escapeHandler(e: KeyboardEvent) {
        if (e.key === "Escape") {
            e.preventDefault()
            const modal = document.querySelector(".sentry-error-embed-wrapper")
            modal?.remove()
            document.removeEventListener("keydown", escapeHandler)
        }
    }
}